/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { CalendarTargetController } from '../../content/controller/calendar-target-controller';
import { EmptyContentController } from '../../content/controller/empty-content-controller';
import { emptyScheduleContentInfo } from '../../schedule/schedule-info';

/**
 * @import EventEmitter from 'eventemitter3'
 * @import { ViewerManagerController } from '.'
 * @import { IAnalyticsTracker } from '../../analytics'
 * @import { Content } from '../../content/content'
 * @import { ContentController, TargetController } from '../../content/controller'
 * @import { ContentControllerFactory } from '../../content/controller/'
 * @import { ScreenId } from '../../content/info'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { ContentTask } from '../../content/task/content-task'
 * @import { ContentTaskCronFactory } from '../../content/task/cron/content-task-cron-factory'
 * @import { Logger } from '../../logger/logger'
 * @import { ScheduleContentInfo } from '../../schedule/schedule-info'
 */

/** @type {ScreenId} */
export const defaultScreenId = 'DISPLAY1';

const defaultScreenInitTarget = `${screenIdToTargetPrefix(
  defaultScreenId
)}.primary`;

/**
 * @implements {ViewerManagerController}
 */
export default class BaseViewerManagerController {
  /**
   * @param {IAnalyticsTracker} analyticsTracker
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {ContentTaskCronFactory} contentTaskCronFactory
   * @param {HTMLElement} el
   * @param {EventEmitter | undefined} eventEmitter
   * @param {Logger} logger
   */
  constructor(
    analyticsTracker,
    contentControllerFactory,
    contentInfo,
    contentTaskCronFactory,
    el,
    eventEmitter,
    logger
  ) {
    this.analyticsTracker = analyticsTracker;
    this.contentControllerFactory = contentControllerFactory;
    this.contentInfo = contentInfo;
    this.contentTaskCronFactory = contentTaskCronFactory;
    this.el = el;
    this.logger = logger;

    /** @type {ContentController | undefined} */
    this.contentController = undefined;
    this.contentTaskCron = null;
    this.display1AvailableTargets = getAvailableTargets(
      screenIdToTargetPrefix(defaultScreenId),
      contentInfo.contentTasks
    );
    this.display1InitTarget =
      getInitTarget(defaultScreenInitTarget, this.display1AvailableTargets) ??
      defaultScreenInitTarget;

    /** @type {TargetController | undefined} */
    this.targetController = undefined;

    /** @private @type {EventEmitter | undefined} */
    this.emitter = eventEmitter;

    this.onPlaying = this.onPlaying.bind(this);
    this.onContentChanged = this.onContentChanged.bind(this);
  }

  /** @type {ViewerManagerController['off']} */
  off(...args) {
    this.emitter?.off(...args);
  }

  /** @type {ViewerManagerController['on']} */
  on(...args) {
    this.emitter?.on(...args);
  }

  /**
   * Vrátí aktuální ContentInfo nebo prázdný objekt.
   * @param {ContentInfo | null} contentInfo
   * @returns {ScheduleContentInfo}
   */
  getScheduleContentInfo(contentInfo) {
    return contentInfo ?? emptyScheduleContentInfo;
  }

  /**
   * @param {Content | null} content
   */
  async onContentChanged(content) {
    await this.stopContentController();
    this.emitter?.emit('content', content);
    await this.createContentController(content);
    try {
      await this.contentController?.start(0);
    } catch {
      // Ignorování chyby.
    }
  }

  /**
   * @private
   * @param {Content} contentModel
   */
  async onPlaying(contentModel) {
    await this.analyticsTracker.trackAnalytics(contentModel).catch(() => {});
    this.emitter?.emit('playing', contentModel);
  }

  /**
   * @param {ContentTask[]} contentTasks
   */
  async preload(contentTasks) {
    // TODO: pridat do TargetRegistry.
    const targetController = new CalendarTargetController(
      this.contentTaskCronFactory,
      contentTasks
    );
    targetController.on('contentChanged', this.onContentChanged);
    this.targetController = targetController;

    const content = targetController.getContent();
    this.emitter?.emit('content', content);
    await this.createContentController(content);
  }

  /**
   * @type {ViewerManagerController['start']}
   */
  async start() {
    try {
      await this.contentController?.start(0);
    } catch {
      // Ignorování chyby.
    }
  }

  /**
   * @type {ViewerManagerController['stop']}
   */
  async stop() {
    await this.stopContentController();
    this.targetController?.off('contentChanged', this.onContentChanged);
    this.targetController = undefined;
  }

  /**
   * @param {Content | null} content
   */
  async createContentController(content) {
    /** @type {ContentController | undefined} */
    let contentController;
    try {
      contentController = this.contentControllerFactory.createForRegion(
        this.el,
        content,
        { contentInfo: this.contentInfo, volume: 1 }
      );
      contentController.on('playing', this.onPlaying);
      await contentController.preload();
      this.contentController = contentController;
    } catch (cause) {
      this.logger.error(
        new Error('Failed to create content controller', { cause })
      );
      contentController = new EmptyContentController();
    }
  }

  async stopContentController() {
    await this.contentController?.stop();
    this.contentController?.off('playing', this.onPlaying);
    this.contentController = undefined;
  }
}

/**
 * Vytvoří pole bez duplicitních názvů targetů.
 * @param {string} id Identifikátor displeje.
 * @param {ContentTask[]} contentTasks
 * @returns {string[]}
 */
export function getAvailableTargets(id, contentTasks) {
  return Array.from([
    ...new Set(
      contentTasks
        .filter(({ target }) => target.startsWith(`${id}.`))
        .map(({ target }) => target)
    )
  ]);
}

/**
 * @param {ContentTask[]} contentTasks
 * @param {string} target
 * @returns {ContentTask[]}
 */
export function getContentTasks(contentTasks, target) {
  return contentTasks.filter((task) => task.target === target);
}

/**
 * Vybere vhodný existující startovní target.
 * @param {string} initTarget
 * @param {string[]} targets
 * @returns {string | undefined}
 */
export function getInitTarget(initTarget, targets) {
  if (targets.length === 0) {
    // Nebyly zadány žádné targety, tak vrátím
    return undefined;
  }
  const targetIdx = targets.findIndex((target) => target === initTarget);
  if (targetIdx === -1) {
    // Pokud existuje pouze jeden target, tak jej vrátíme bez filtrování.
    // Pokud bylo zadáno více targetů, ale žádný neodpovídá filtru, tak vrátím první v pořadí.
    return targets[0];
  }

  // Target byl nalezený, tak ho vrátím jako vhodný.
  return targets[targetIdx];
}

/**
 * Převede identifikátor obrazovky na prefix targetu.
 *
 * TODO: Toto mapování je dáno nepsanou konvencí, která ale po zavedení
 * programovatelného vytváření a registrací oken může přestat platit!
 * @param {ScreenId} screenId Identifikátor obrazovky, např. DISPLAY1.
 * @returns {string} Prefix targetu před tečkou.
 */
function screenIdToTargetPrefix(screenId) {
  return screenId.toLowerCase();
}
